import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageState from '../components/PageState';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Tooltip,
  Divider,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Upload,
  Download,
  LockReset,
  People as PeopleIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Description as FormIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { User, UserRole } from '../types';
import { entraAPI, oktaAPI, usersAPI, type CreateUserPayload } from '../services/api';
import { UI_TOKENS } from '../constants/uiTokens';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import { chipSxFromSurface, getRoleChipStyle, getStatusSurface } from '../utils/appearanceTokens';
import { useTheme as useMuiTheme } from '@mui/material/styles';

// ─── Style tokens ─────────────────────────────────────────────────────────────
const INPUT_SX = {
  '& .MuiInputBase-input': { fontSize: '0.82rem' },
  '& .MuiInputLabel-root': { fontSize: '0.82rem' },
  '& .MuiSelect-select': { fontSize: '0.82rem' },
};
const MENU_PROPS = {
  PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.82rem', minHeight: 32, py: 0.5 } } },
};

const TH_SX = {
  fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' as const,
  letterSpacing: 0.5, color: 'text.secondary', bgcolor: 'grey.50',
  borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' as const, py: 1,
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

type IdpImportSource = 'okta' | 'azure';

type IdpDirectoryUser = {
  id: string;
  status: string;
  profile: { login: string; email: string; firstName: string; lastName: string; displayName: string };
};

const IDP_IMPORT_OPTIONS: Array<{ value: IdpImportSource; label: string }> = [
  { value: 'okta', label: 'Okta' },
  { value: 'azure', label: 'Microsoft Entra ID' },
];

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const mode = muiTheme.palette.mode;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const [idpDialogOpen, setIdpDialogOpen] = useState(false);
  const [idpLoading, setIdpLoading] = useState(false);
  const [idpUsers, setIdpUsers] = useState<IdpDirectoryUser[]>([]);
  const [selectedIdp, setSelectedIdp] = useState<IdpImportSource>('okta');
  const [idpQuery, setIdpQuery] = useState('');
  const [idpSelected, setIdpSelected] = useState<Record<string, boolean>>({});
  const [idpImportRole, setIdpImportRole] = useState<UserRole>('user');

  // Reset password (admin)
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState<User | null>(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdConfirm, setResetPwdConfirm] = useState('');
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  const [suspendUser, setSuspendUser] = useState<User | null>(null);
  const [suspendLoading, setSuspendLoading] = useState(false);

  const [createPassword, setCreatePassword] = useState('');
  const [createPasswordConfirm, setCreatePasswordConfirm] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'user' as UserRole,
    is_active: true,
  });

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { setPage(1); }, [searchTerm, roleFilter, statusFilter, rowsPerPage, listingSort]);

  const USER_MUTATION_ROLES: UserRole[] = ['super_admin', 'app_admin', 'iga_admin', 'pam_admin'];
  const PROFILE_VIEW_ROLES: UserRole[] = ['super_admin', 'app_admin', 'sso_admin', 'iga_admin', 'pam_admin'];
  const canMutateUsers =
    !!currentUser?.role && USER_MUTATION_ROLES.includes(currentUser.role as UserRole);
  const canOpenProfile = (userId: string) =>
    userId === currentUser?.id || (!!currentUser?.role && PROFILE_VIEW_ROLES.includes(currentUser.role as UserRole));
  const narrowList = useMediaQuery('(max-width:767px)');

  const filteredUsers = users.filter((u) => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q);
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? u.is_active : !u.is_active);
    return matchSearch && matchRole && matchStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedUsers = sortListing(filteredUsers, listingSort).slice(startIndex, startIndex + rowsPerPage);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersAPI.getUsers();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role,
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({ username: '', email: '', first_name: '', last_name: '', role: 'user', is_active: true });
      setCreatePassword('');
      setCreatePasswordConfirm('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ username: '', email: '', first_name: '', last_name: '', role: 'user', is_active: true });
    setCreatePassword('');
    setCreatePasswordConfirm('');
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await usersAPI.updateUser(editingUser.id, formData);
        setSuccess('User updated successfully');
      } else {
        if (!createPassword.trim()) {
          setError('Password is required');
          return;
        }
        if (createPassword.length < 8) {
          setError('Password must be at least 8 characters');
          return;
        }
        if (createPassword !== createPasswordConfirm) {
          setError('Password and confirm password do not match');
          return;
        }
        const payload: CreateUserPayload = { ...formData, password: createPassword };
        await usersAPI.createUser(payload);
        setSuccess('User created successfully');
      }
      await loadUsers();
      handleCloseDialog();
    } catch (err: unknown) {
      const api = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setError(api?.error || api?.message || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.deleteUser(userId);
        await loadUsers();
        setSuccess('User deleted successfully');
      } catch {
        setError('Failed to delete user');
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await usersAPI.downloadTemplate();
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'users_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('Template downloaded');
    } catch {
      setError('Failed to download template');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    try {
      setImportLoading(true);
      setImportProgress(0);
      setError('');
      setSuccess('');
      const progressInterval = setInterval(() => {
        setImportProgress(prev => { if (prev >= 90) { clearInterval(progressInterval); return 90; } return prev + 10; });
      }, 200);
      const fd = new FormData();
      fd.append('file', file);
      const result = await usersAPI.importUsers(fd);
      clearInterval(progressInterval);
      setImportProgress(100);
      if (result.success) {
        setSuccess(`Import completed! ${result.successfulImports} users imported, ${result.failedImports} failed.`);
        await loadUsers();
      } else {
        setError('Import failed');
      }
      if (result.errors?.length > 0) {
        const errPreview = result.errors.slice(0, 5).join('; ');
        setError(
          result.errors.length <= 5
            ? `Import completed with issues: ${errPreview}`
            : `Import completed with ${result.errors.length} issues. First few: ${errPreview}`
        );
      }
    } catch (err: any) {
      const api = err.response?.data;
      const validationErrors = api?.data?.errors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const preview = validationErrors.slice(0, 5).join('; ');
        setError(
          validationErrors.length <= 5
            ? `Validation failed: ${preview}`
            : `Validation failed (${validationErrors.length} errors). First few: ${preview}`
        );
      } else {
        setError(api?.error || 'Failed to import users');
      }
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      if (event.target) event.target.value = '';
    }
  };

  const loadIdpUsers = async (q?: string, idp: IdpImportSource = selectedIdp) => {
    try {
      setIdpLoading(true);
      setError('');
      const params = { q: (q ?? idpQuery).trim(), limit: 50 };
      const res =
        idp === 'okta'
          ? await oktaAPI.listUsers(params)
          : await entraAPI.listUsers(params);
      setIdpUsers(res.data.data?.users || []);
      setIdpSelected({});
    } catch (e: any) {
      const api = e?.response?.data;
      const idpLabel = IDP_IMPORT_OPTIONS.find((o) => o.value === idp)?.label || 'IdP';
      setError(api?.message || api?.error || `Failed to load users from ${idpLabel}`);
      setIdpUsers([]);
      setIdpSelected({});
    } finally {
      setIdpLoading(false);
    }
  };

  const openIdpDialog = async () => {
    setIdpDialogOpen(true);
    setIdpQuery('');
    setSelectedIdp('okta');
    await loadIdpUsers('', 'okta');
  };

  const handleIdpSourceChange = async (idp: IdpImportSource) => {
    setSelectedIdp(idp);
    setIdpQuery('');
    setIdpUsers([]);
    setIdpSelected({});
    await loadIdpUsers('', idp);
  };

  // Add-user menu (matches Applications page pattern)
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleIdpUser = (id: string, checked: boolean) => {
    setIdpSelected((prev) => ({ ...prev, [id]: checked }));
  };

  const toggleSelectAllIdp = (checked: boolean) => {
    if (!checked) {
      setIdpSelected({});
      return;
    }
    const next: Record<string, boolean> = {};
    idpUsers.forEach((u) => { next[u.id] = true; });
    setIdpSelected(next);
  };

  const handleImportSelectedFromIdp = async () => {
    const selected = idpUsers.filter((u) => idpSelected[u.id]);
    if (selected.length === 0) {
      setError('Select at least 1 user to import');
      return;
    }
    try {
      setImportLoading(true);
      setError('');
      setSuccess('');
      const payload = selected.map((u) => ({
        username: u.profile.login || u.profile.email,
        email: u.profile.email || u.profile.login,
        first_name: u.profile.firstName || 'User',
        last_name: u.profile.lastName || '',
        role: idpImportRole,
      }));
      const result = await usersAPI.importUsersFromIdp(payload);
      setSuccess(`Imported ${result.imported} user(s). Skipped ${result.skipped}.`);
      if (result.errors?.length) {
        const preview = result.errors.slice(0, 5).join('; ');
        setError(result.errors.length <= 5 ? preview : `Some rows failed (${result.errors.length}). First few: ${preview}`);
      }
      setIdpDialogOpen(false);
      await loadUsers();
    } catch (e: any) {
      const api = e?.response?.data;
      setError(api?.error || api?.message || 'Failed to import users from IdP');
    } finally {
      setImportLoading(false);
    }
  };

  const openResetPassword = (u: User) => {
    setError('');
    setSuccess('');
    setResetPwdUser(u);
    setResetPwdValue('');
    setResetPwdConfirm('');
    setResetPwdOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetPwdUser) return;
    if (!resetPwdValue) {
      setError('New password is required');
      return;
    }
    if (resetPwdValue.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (resetPwdValue !== resetPwdConfirm) {
      setError('New password and confirm password do not match');
      return;
    }
    try {
      setResetPwdLoading(true);
      setError('');
      await usersAPI.resetUserPassword(resetPwdUser.id, resetPwdValue);
      setSuccess(`Password reset for ${resetPwdUser.username}`);
      setResetPwdOpen(false);
    } catch (e: any) {
      const api = e?.response?.data;
      setError(api?.error || api?.message || 'Failed to reset password');
    } finally {
      setResetPwdLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>User Management</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              {filteredUsers.length === 1 ? '1 user' : `${filteredUsers.length} users`}
            </Typography>
          </Box>
        </Box>
        {canMutateUsers && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
            />
            <Button
              size="small"
              variant="contained"
              startIcon={<Add sx={{ fontSize: '0.9rem !important' }} />}
              endIcon={<ArrowDownIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY, borderRadius: 2, fontWeight: 700 }}
            >
              Add User
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 200, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
            >
              <MenuItem
                onClick={() => { setAddMenuAnchor(null); handleOpenDialog(); }}
                sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
              >
                <ListItemIcon><FormIcon sx={{ fontSize: '1rem', color: 'primary.main' }} /></ListItemIcon>
                <ListItemText primary="Use Form" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
              </MenuItem>
              <MenuItem
                onClick={() => { setAddMenuAnchor(null); fileInputRef.current?.click(); }}
                disabled={importLoading}
                sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
              >
                <ListItemIcon><Upload sx={{ fontSize: '1rem', color: 'success.main' }} /></ListItemIcon>
                <ListItemText primary="Import from Excel" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  setAddMenuAnchor(null);
                  await openIdpDialog();
                }}
                disabled={importLoading}
                sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
              >
                <ListItemIcon><CloudUploadIcon sx={{ fontSize: '1rem', color: 'warning.main' }} /></ListItemIcon>
                <ListItemText primary="Import from IdP" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => { setAddMenuAnchor(null); void handleDownloadTemplate(); }}
                sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
              >
                <ListItemIcon><Download sx={{ fontSize: '1rem', color: 'text.secondary' }} /></ListItemIcon>
                <ListItemText primary="Download template" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Import from IdP Dialog */}
      <Dialog open={idpDialogOpen} onClose={() => setIdpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2.5,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontWeight: 800,
            fontSize: '1rem',
          }}
        >
          Import users from IdP
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, bgcolor: 'action.hover' }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 220, ...INPUT_SX }}>
                <InputLabel>Identity Provider</InputLabel>
                <Select
                  value={selectedIdp}
                  label="Identity Provider"
                  onChange={(e) => handleIdpSourceChange(e.target.value as IdpImportSource)}
                  MenuProps={MENU_PROPS}
                >
                  {IDP_IMPORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search"
                placeholder={`Search ${IDP_IMPORT_OPTIONS.find((o) => o.value === selectedIdp)?.label || 'IdP'} directory`}
                value={idpQuery}
                onChange={(e) => setIdpQuery(e.target.value)}
                sx={{ flex: 1, minWidth: 260, ...INPUT_SX }}
              />
              <FormControl size="small" sx={{ minWidth: 180, ...INPUT_SX }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={idpImportRole}
                  label="Role"
                  onChange={(e) => setIdpImportRole(e.target.value as UserRole)}
                  MenuProps={MENU_PROPS}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="app_admin">App Admin</MenuItem>
                  <MenuItem value="iga_admin">IGA Admin</MenuItem>
                  <MenuItem value="sso_admin">SSO Admin</MenuItem>
                  <MenuItem value="pam_admin">PAM Admin</MenuItem>
                  <MenuItem value="grc_admin">GRC Admin</MenuItem>
                  <MenuItem value="auditor">Auditor</MenuItem>
                  <MenuItem value="delegate">Delegate</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={() => loadIdpUsers(idpQuery, selectedIdp)}
                disabled={idpLoading}
                sx={{ borderRadius: 2, fontSize: '0.78rem', py: 0.5 }}
              >
                Refresh
              </Button>
            </Box>
          </Paper>

          {idpLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={idpUsers.length > 0 && idpUsers.every((u) => !!idpSelected[u.id])}
                        indeterminate={idpUsers.some((u) => !!idpSelected[u.id]) && !idpUsers.every((u) => !!idpSelected[u.id])}
                        onChange={(e) => toggleSelectAllIdp(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={TH_SX}>Name</TableCell>
                    <TableCell sx={TH_SX}>Login</TableCell>
                    <TableCell sx={TH_SX}>Email</TableCell>
                    <TableCell sx={TH_SX}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {idpUsers.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!idpSelected[u.id]} onChange={(e) => toggleIdpUser(u.id, e.target.checked)} />
                      </TableCell>
                      <TableCell>
                        {(u.profile.firstName || u.profile.lastName)
                          ? `${u.profile.firstName} ${u.profile.lastName}`.trim()
                          : (u.profile.displayName || '—')}
                      </TableCell>
                      <TableCell>{u.profile.login || '—'}</TableCell>
                      <TableCell>{u.profile.email || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={u.status || '—'}
                          size="small"
                          sx={{
                            height: 22,
                            borderRadius: 999,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            ...chipSxFromSurface(
                              getStatusSurface(
                                String(u.status || '').toUpperCase() === 'ACTIVE' ? 'success' : 'neutral',
                                mode
                              )
                            ),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {idpUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No users found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Button variant="outlined" onClick={() => setIdpDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleImportSelectedFromIdp} disabled={importLoading || idpLoading} sx={{ borderRadius: 2, fontWeight: 700 }}>
            Import selected
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Progress */}
      {importLoading && (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ py: '10px !important', px: '14px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: '0.82rem' }}>Importing users… {importProgress}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={importProgress} sx={{ height: 4, borderRadius: 2 }} />
          </CardContent>
        </Card>
      )}

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.82rem' }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, py: 0.5, fontSize: '0.82rem' }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, p: '10px 14px' }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name, email or username…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: '1rem', color: 'text.secondary' }} /></InputAdornment> }}
              sx={{ minWidth: 280, ...INPUT_SX }}
            />
            <FormControl size="small" sx={{ minWidth: 150, ...INPUT_SX }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
                <MenuItem value="app_admin">App Admin</MenuItem>
                <MenuItem value="iga_admin">IGA Admin</MenuItem>
                <MenuItem value="sso_admin">SSO Admin</MenuItem>
                <MenuItem value="pam_admin">PAM Admin</MenuItem>
                <MenuItem value="grc_admin">GRC Admin</MenuItem>
                <MenuItem value="auditor">Auditor</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120, ...INPUT_SX }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as any)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <ListingSortSelect
              value={listingSort}
              onChange={setListingSort}
              sx={INPUT_SX}
              menuProps={MENU_PROPS}
            />
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', ml: 'auto' }}>
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {error && users.length === 0 && <PageState error={error}>{null}</PageState>}
        {narrowList && !(error && users.length === 0) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5 }}>
            {loading && <PageState loading>{null}</PageState>}
            {!loading && paginatedUsers.length === 0 && (
              <PageState empty emptyMessage="No users match the current filters.">{null}</PageState>
            )}
            {paginatedUsers.map((user) => {
              const rs = getRoleChipStyle(user.role, mode);
              return (
                <Card key={user.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      {user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.username}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1 }}>{user.email}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={rs.label} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, ...chipSxFromSurface(rs) }} />
                      <Chip label={user.is_active ? 'Active' : 'Inactive'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user.sign_in_source || '—'}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never signed in'}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', display: narrowList ? 'none' : 'block' }}>
          <Table stickyHeader size="small" sx={{
            ...listingTableSx,
            '@media (max-width: 767px)': {
              '& thead': { display: 'none' },
              '& tbody tr': { display: 'block', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 },
              '& tbody td': { display: 'block', borderBottom: 'none' },
            },
          }}>
            <TableHead>
              <TableRow>
                {['User', 'Email', 'Role', 'Status', 'Source', 'Last login', 'Created Date', ...(canMutateUsers ? ['Actions'] : [])].map((col) => (
                  <TableCell key={col} sx={TH_SX}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(canMutateUsers ? 8 : 7)].map((__, j) => (
                      <TableCell key={j}><Skeleton height={28} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canMutateUsers ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No users match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user, idx) => {
                  const rs = getRoleChipStyle(user.role, mode);
                  const isSelf = user.id === currentUser?.id;
                  const statusSurface = getStatusSurface(user.is_active ? 'success' : 'neutral', mode);
                  return (
                    <TableRow key={user.id} hover sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50', '&:hover': { bgcolor: 'action.hover' } }}>
                      {/* User */}
                      <TableCell sx={{ py: 0.75 }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1,
                            ...(canOpenProfile(user.id) && { cursor: 'pointer' }),
                          }}
                          onClick={canOpenProfile(user.id) ? () => navigate(`/user-profile/${user.id}`) : undefined}
                        >
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main', flexShrink: 0 }}>
                            {(user.first_name || user.username || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.3,
                              ...(canOpenProfile(user.id) && { '&:hover': { textDecoration: 'underline', color: 'primary.main' } }),
                            }}>
                              {user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.username}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>@{user.username}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Email */}
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{user.email}</Typography>
                      </TableCell>

                      {/* Role */}
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip label={rs.label} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, ...chipSxFromSurface(rs), '& .MuiChip-label': { px: 1 } }} />
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.7rem', fontWeight: 600,
                            ...chipSxFromSurface(statusSurface),
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ py: 0.75 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                          {user.identity_source || user.sign_in_source || '—'}
                          {user.groups ? ` · ${user.groups}` : ' · No group'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </Typography>
                      </TableCell>

                      {/* Created Date */}
                      <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                          {formatDate((user as any).created_at)}
                        </Typography>
                      </TableCell>

                      {canMutateUsers && (
                        <TableCell sx={{ py: 0.75 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit user">
                              <span>
                            <Button
                              size="small"
                              color="warning"
                              disabled={isSelf || user.username === 'provysn_super_admin'}
                              onClick={() => setSuspendUser(user)}
                              sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                            >
                              Suspend
                            </Button>
                                <IconButton size="small" aria-label="Edit user" onClick={() => handleOpenDialog(user)} disabled={isSelf} sx={{ p: 0.5 }}>
                                  <Edit sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reset password">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Reset password"
                                  onClick={() => openResetPassword(user)}
                                  disabled={isSelf}
                                  sx={{ p: 0.5 }}
                                >
                                  <LockReset sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete user">
                              <span>
                                <IconButton size="small" color="error" aria-label="Delete user" onClick={() => handleDeleteUser(user.id)} disabled={isSelf} sx={{ p: 0.5 }}>
                                  <Delete sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <ListingPagination
          page={page}
          totalPages={totalPages}
          totalItems={filteredUsers.length}
          pageSize={rowsPerPage}
          onPageChange={setPage}
          onPageSizeChange={setRowsPerPage}
          pageSizeSelectSx={INPUT_SX}
          pageSizeMenuProps={MENU_PROPS}
        />
      </Paper>

      {/* Add or edit user dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          window.setTimeout(handleCloseDialog, 0);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, fontSize: '1rem', fontWeight: 700 }}>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" label="Username" value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth required disabled={!!editingUser} sx={INPUT_SX} />
            <TextField size="small" label="Email" type="email" value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth required sx={INPUT_SX} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField size="small" label="First Name" value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                fullWidth sx={INPUT_SX} />
              <TextField size="small" label="Last Name" value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                fullWidth sx={INPUT_SX} />
            </Box>
            {!editingUser && (
              <>
                <TextField
                  size="small"
                  label="Password"
                  type="password"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  fullWidth
                  required
                  helperText="Minimum 8 characters"
                  sx={INPUT_SX}
                  autoComplete="new-password"
                />
                <TextField
                  size="small"
                  label="Confirm password"
                  type="password"
                  value={createPasswordConfirm}
                  onChange={(e) => setCreatePasswordConfirm(e.target.value)}
                  fullWidth
                  required
                  sx={INPUT_SX}
                  autoComplete="new-password"
                />
              </>
            )}
            <FormControl size="small" fullWidth sx={INPUT_SX}>
              <InputLabel>Role</InputLabel>
              <Select value={formData.role} label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                MenuProps={MENU_PROPS}>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="app_admin">App Admin</MenuItem>
                <MenuItem value="iga_admin">IGA Admin</MenuItem>
                <MenuItem value="sso_admin">SSO Admin</MenuItem>
                <MenuItem value="pam_admin">PAM Admin</MenuItem>
                <MenuItem value="grc_admin">GRC Admin</MenuItem>
                <MenuItem value="auditor">Auditor</MenuItem>
                {currentUser?.role === 'super_admin' && <MenuItem value="super_admin">Super Admin</MenuItem>}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} size="small" />}
              label={<Typography sx={{ fontSize: '0.82rem' }}>Active</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              window.setTimeout(handleCloseDialog, 0);
            }}
            sx={{ fontSize: '0.82rem' }}
          >
            Cancel
          </Button>
          <Button size="small" onClick={handleSubmit} variant="contained" sx={{ fontSize: '0.82rem' }}>
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwdOpen} onClose={() => setResetPwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, fontSize: '1rem', fontWeight: 700 }}>
          Reset Password
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 1 }}>
            {resetPwdUser ? `Set a new password for ${resetPwdUser.username}` : 'Set a new password'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              label="New password"
              type="password"
              value={resetPwdValue}
              onChange={(e) => setResetPwdValue(e.target.value)}
              fullWidth
              helperText="Minimum 8 characters"
              sx={INPUT_SX}
            />
            <TextField
              size="small"
              label="Confirm new password"
              type="password"
              value={resetPwdConfirm}
              onChange={(e) => setResetPwdConfirm(e.target.value)}
              fullWidth
              sx={INPUT_SX}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPwdOpen(false)} disabled={resetPwdLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={resetPwdLoading}>
            {resetPwdLoading ? 'Saving…' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!suspendUser} onClose={() => { if (!suspendLoading) setSuspendUser(null); }}>
        <DialogTitle>Suspend {suspendUser?.username}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem' }}>
            This person cannot sign in until an administrator activates the account again. Confirm to suspend.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendUser(null)} disabled={suspendLoading}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={suspendLoading}
            onClick={async () => {
              if (!suspendUser) return;
              setSuspendLoading(true);
              try {
                await usersAPI.suspendUser(suspendUser.id);
                setSuspendUser(null);
                await loadUsers();
              } finally {
                setSuspendLoading(false);
              }
            }}
          >
            {suspendLoading ? 'Suspending…' : 'Confirm suspend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
